import { useState } from "react"
import { Delete } from "@mui/icons-material"
import { FormikErrors, useFormikContext } from "formik"
import {
    Box,
    Checkbox,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    Grid,
    IconButton,
    Stack,
    TextField,
    Typography,
} from "@mui/material"
import {
    fetchMerchantAdmins,
    FetchMerchantAdminsResponse,
    MerchantQuery,
} from "../../../../../../merchant/admin/merchantAdminApi"
import { StandardButton } from "../../../../../../../shared/components/buttons/StandardButton"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { BeginOnboardingFormValues } from "../../supplierMerchantsTypes"
import { useMerchant } from "../../../../../../merchants/merchantHooks"
import { useApiQuery } from "../../../../../../../core/api/useApiQuery"
import { UserType } from "../../../../../../login/loginTypes"
import { useSupplier } from "../../../../../supplierHooks"
import {
    fetchSupplierAdmins,
    FetchSupplierAdminsResponse,
    SupplierAdminsQuery,
} from "../../../../account/administrators/supplierAdminApi"

function transformSupplierData(data: FetchSupplierAdminsResponse) {
    return data.administrators
}

function transformMerchantData(data: FetchMerchantAdminsResponse) {
    return data.administrators
}

export function SecondStep() {
    const { merchantId } = useMerchant()
    const { supplierId } = useSupplier()
    const [email, setEmail] = useState("")

    const { values, setFieldValue, errors } = useFormikContext<BeginOnboardingFormValues>()

    const { data: supplierAdmins = [] } = useApiQuery(fetchSupplierAdmins, {
        queryName: SupplierAdminsQuery.ADMINS_FOR_SUPPLIER,
        dependencies: {
            supplierId,
            userType: UserType.SUPPLIER,
            isActive: true,
        },
        select: transformSupplierData,
        suspense: true,
        useErrorBoundary: true,
    })

    const { data: merchantAdmins = [] } = useApiQuery(fetchMerchantAdmins, {
        queryName: MerchantQuery.ADMINS_FOR_MERCHANT,
        dependencies: {
            merchantId,
        },
        select: transformMerchantData,
        suspense: true,
        useErrorBoundary: true,
    })

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, administratorId: number) => {
        const updatedAdmins = event.target.checked
            ? [...values.receipients.administrators, { id: administratorId }]
            : values.receipients.administrators.filter((administrator) => administrator.id !== administratorId)

        setFieldValue("receipients.administrators", updatedAdmins)
    }

    const isChecked = (administratorId: number) =>
        values.receipients.administrators.some((administrator) => administrator.id === administratorId)

    const handleAddEmail = () => {
        if (email) {
            setFieldValue("receipients.extraReceivers", [...values.receipients.extraReceivers, { email }])
            setEmail("")
        }
    }

    const handleRemoveEmail = (index: number) => {
        const updatedExtraReceivers = values.receipients.extraReceivers.filter((_, i) => i !== index)
        setFieldValue("receipients.extraReceivers", updatedExtraReceivers)
    }

    return (
        <Stack gap="2rem">
            <Stack gap="1rem">
                <LocalizedStrict id="support-create-supplier-subscription-status-begin-onboarding-dialog-existing-administrators">
                    <Typography variant="h6">Existing administrators</Typography>
                </LocalizedStrict>
                <FormGroup>
                    {!!supplierAdmins.length &&
                        supplierAdmins.map(({ user }) => (
                            <FormControlLabel
                                key={user.id}
                                control={
                                    <Checkbox
                                        checked={isChecked(user.id)}
                                        onChange={(event) => handleCheckboxChange(event, user.id)}
                                    />
                                }
                                label={
                                    <Typography variant="body1">
                                        <LocalizedStrict
                                            id="support-create-supplier-subscription-status-begin-onboarding-dialog-supplier-admin"
                                            vars={{ adminDetails: `${user.displayName} (${user.email})` }}
                                        >
                                            <>Supplier admin:</>
                                        </LocalizedStrict>
                                    </Typography>
                                }
                            />
                        ))}
                    {!!merchantAdmins.length &&
                        merchantAdmins.map(({ user }) => (
                            <FormControlLabel
                                key={user.id}
                                control={
                                    <Checkbox
                                        checked={isChecked(user.id)}
                                        onChange={(event) => handleCheckboxChange(event, user.id)}
                                    />
                                }
                                label={
                                    <Typography variant="body1">
                                        <LocalizedStrict
                                            id="support-create-supplier-subscription-status-begin-onboarding-dialog-kitchen-admin"
                                            vars={{ adminDetails: `${user.displayName} (${user.email})` }}
                                        >
                                            <>Kitchen admin:</>
                                        </LocalizedStrict>
                                    </Typography>
                                }
                            />
                        ))}
                    {errors.receipients?.administrators && typeof errors.receipients.administrators === "string" && (
                        <FormHelperText error>{errors.receipients.administrators}</FormHelperText>
                    )}
                </FormGroup>
            </Stack>
            <Stack gap="1rem">
                <LocalizedStrict id="support-create-supplier-subscription-status-begin-onboarding-dialog-extra-receivers">
                    <Typography variant="h6">Extra receivers</Typography>
                </LocalizedStrict>
                {!!values.receipients.extraReceivers.length && (
                    <Stack spacing={1}>
                        {values.receipients.extraReceivers.map((receiver, index) => (
                            <Stack key={index}>
                                <Grid container alignItems="center" gap="0.5rem">
                                    <IconButton onClick={() => handleRemoveEmail(index)}>
                                        <Delete />
                                    </IconButton>
                                    <Typography variant="body1">{receiver.email}</Typography>
                                </Grid>
                                {errors.receipients?.extraReceivers &&
                                    typeof errors.receipients.extraReceivers[index] === "object" &&
                                    (errors.receipients.extraReceivers[index] as FormikErrors<{ email: string }>)
                                        .email && (
                                        <FormHelperText error>
                                            {
                                                (
                                                    errors.receipients.extraReceivers[index] as FormikErrors<{
                                                        email: string
                                                    }>
                                                ).email
                                            }
                                        </FormHelperText>
                                    )}
                            </Stack>
                        ))}
                    </Stack>
                )}
                <Grid container gap={2} alignItems="center" flexWrap="nowrap">
                    <LocalizedStrict
                        id="support-create-supplier-subscription-status-begin-onboarding-dialog-label-email"
                        attrs={{ label: true, placeholder: true }}
                    >
                        <TextField
                            label="Receiver email"
                            placeholder="Receiver email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            size="small"
                        />
                    </LocalizedStrict>
                    <Box>
                        <LocalizedStrict id="support-create-supplier-subscription-status-begin-onboarding-dialog-button-add">
                            <StandardButton onClick={handleAddEmail} color="primary" size="small">
                                Add
                            </StandardButton>
                        </LocalizedStrict>
                    </Box>
                </Grid>
            </Stack>
            <Grid container justifyContent="center" pt="1rem">
                <LocalizedStrict id="support-create-supplier-subscription-status-begin-onboarding-dialog-button-begin">
                    <StandardButton fullWidth={false} type="submit">
                        Begin
                    </StandardButton>
                </LocalizedStrict>
            </Grid>
        </Stack>
    )
}
